import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { PvInverterIdTypes, ServerPvInverterIds, SolarModule } from 'src/Elements/SystemSettings/SystemSettingsUtils'
import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { AuthFetchWithResponse } from 'src/Types/api'
import { PvInverterModel } from '../models/solar.model'
import { PvInverterDto } from '../dto/pv-inverter.dto'
import { fromInverterDtoToModelTransformer } from '../transformers'

/**
 * Fetch pv-inverter indexes. A storage can have multiple pv-inverters. Thus, in the context of a farm a list of index for each storage is returned.
 *
 * Single Storage Installation: [0, ..., n]
 * Farm Context: {701937000926: [0, ..., n], 822229000042: [0, ..., n]}
 *
 * Note: In the context of a farm clients can be selected. Since those represent a single storage they also behave as such.
 *
 * @param storageId
 * @param isFarmMasterSelected
 * @param authFetch
 * @returns
 */
export const fetchPvInverterIndexes = async (
  storageId: string | undefined,
  isFarmMasterSelected: boolean,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<PvInverterIdTypes, AuthFetchError | Error>> => {
  const pathClient = `storages/${storageId}/hardware/pv-inverter/overview`
  const pathServer = `storages/${storageId}/hardware/pv-inverter/overview/master`

  try {
    const allIndexesResponse = await authFetch<PvInverterIdTypes>({
      path: isFarmMasterSelected ? pathServer : pathClient,
      method: 'GET',
    })

    if (allIndexesResponse.status === StatusCodes.OK) {
      return new ApiResult<PvInverterIdTypes, AuthFetchError | Error>(allIndexesResponse.data, false)
    }
    return new ApiResult<PvInverterIdTypes, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<PvInverterIdTypes>(error)
  }
}

/**
 * Fetch pv-invert in the context of a single storage installation.
 *
 * @param storageId
 * @param pvInverterIds
 * @param authFetch
 * @returns
 */
export const fetchPvInverters = async (
  storageId: string | undefined,
  pvInverterIds: number[],
  authFetch: AuthFetchWithResponse
): Promise<Array<ApiResult<SolarModule, AuthFetchError | Error>>> => {
  try {
    const solarModulePromises = pvInverterIds.map(async pvIndex => {
      const solarModuleResponse = await authFetch<SolarModule>({
        path: `storages/${storageId}/hardware/pv-inverter/overview/${pvIndex}`,
        method: 'GET',
      })

      if (solarModuleResponse.status === StatusCodes.OK) {
        solarModuleResponse.data.storageId = storageId
        return new ApiResult<SolarModule, AuthFetchError | Error>(solarModuleResponse.data, false)
      }
      return new ApiResult<SolarModule, AuthFetchError | Error>(null, true)
    })
    return Promise.all(solarModulePromises)
  } catch (error) {
    return [handleFetchError<SolarModule>(error)]
  }
}

/**
 * Fetched pv-invert in the context of a farm. In other words: all pv-invert of all clients.
 *
 * @param serverPvInverterIds
 * @param authFetch
 * @returns
 */
export const fetchFarmServerPvInverters = async (
  serverPvInverterIds: ServerPvInverterIds,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<SolarModule, AuthFetchError | Error>[] | ApiResult<SolarModule, AuthFetchError | Error>[]> => {
  const solarPromises: Promise<void>[] = []
  const values: ApiResult<SolarModule, AuthFetchError | Error>[] = []
  try {
    Object.entries(serverPvInverterIds).forEach(async ([clientStorageId, clientPvIndexes]) => {
      const promises = clientPvIndexes.map(async (pvIndex: number) => {
        const solarModuleResponse = await authFetch<SolarModule>({
          path: `storages/${clientStorageId}/hardware/pv-inverter/overview/${pvIndex}`,
          method: 'GET',
        })

        if (solarModuleResponse.status === StatusCodes.OK) {
          solarModuleResponse.data.storageId = clientStorageId
          values.push(new ApiResult<SolarModule, AuthFetchError | Error>(solarModuleResponse.data, false))
        }
        values.push(new ApiResult<SolarModule, AuthFetchError | Error>(null, true))
      })
      solarPromises.push(...promises)
    })
    await Promise.allSettled(solarPromises)
    return values
  } catch (error) {
    return [handleFetchError<SolarModule>(error)]
  }
}

export const fetchPvInverterList = async (
  storageId: string | undefined,
  authFetch: AuthFetchWithResponse,
  abortSignal?: AbortSignal
): Promise<ApiResult<PvInverterModel[], Error | AuthFetchError>> => {
  try {
    const response = await authFetch<PvInverterDto>({
      path: `storages/${storageId}/hardware/pv-inverter`,
      method: 'GET',
      abortSignal,
    })

    if (response.status === StatusCodes.OK) {
      const transformedData = fromInverterDtoToModelTransformer(response.data)
      /* Currently we seem to only ever have one inverter, so the BE only returns one object, but there might be more in the future so we were asked to prepare for a list of inverters as best as possible, so working with arrays from here on */
      return new ApiResult<PvInverterModel[], Error | AuthFetchError>([transformedData], false)
    }

    return new ApiResult<PvInverterModel[], Error | AuthFetchError>(null, true)
  } catch (error) {
    console.error(error)
    return handleFetchError<PvInverterModel[]>(error)
  }
}
